<template>
  <div class="puzzle-game">
    <!-- Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h3 class="fw-bold text-primary">🧩 Daily Puzzle</h3>
        <p class="text-muted mb-0">Challenge your mind with today's puzzle</p>
      </div>
      <button class="btn btn-outline-secondary" @click="$router.go(-1)">
        <i class="bi bi-arrow-left me-2"></i>Back
      </button>
    </div>

    <!-- Puzzle Selection -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="modern-card p-4">
          <h5 class="fw-bold mb-3">Choose Your Puzzle</h5>
          <div class="row g-3">
            <div 
              v-for="puzzle in puzzles" 
              :key="puzzle.id"
              class="col-md-4"
            >
              <div 
                class="puzzle-card p-4 text-center cursor-pointer"
                :class="{ active: selectedPuzzle?.id === puzzle.id }"
                @click="selectPuzzle(puzzle)"
              >
                <div class="puzzle-icon mb-3">{{ puzzle.icon }}</div>
                <h6 class="fw-bold mb-2">{{ puzzle.title }}</h6>
                <p class="text-muted small mb-3">{{ puzzle.description }}</p>
                <div class="difficulty-badge">
                  <span class="badge" :class="getDifficultyClass(puzzle.difficulty)">
                    {{ puzzle.difficulty }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Active Puzzle -->
    <div v-if="selectedPuzzle" class="row">
      <div class="col-lg-8">
        <div class="modern-card p-4">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="fw-bold">{{ selectedPuzzle.title }}</h5>
            <div class="puzzle-timer">
              <i class="bi bi-clock me-2"></i>{{ formatTime(elapsedTime) }}
            </div>
          </div>

          <!-- Word Scramble Puzzle -->
          <div v-if="selectedPuzzle.type === 'word-scramble'" class="puzzle-content">
            <div class="puzzle-question mb-4">
              <h6 class="fw-bold">Unscramble the word:</h6>
              <p class="text-muted">{{ selectedPuzzle.hint }}</p>
            </div>
            
            <div class="scrambled-word mb-4">
              <div class="letter-tiles d-flex justify-content-center gap-2 mb-3">
                <div 
                  v-for="(letter, index) in scrambledLetters" 
                  :key="index"
                  class="letter-tile"
                  :class="{ selected: selectedLetters.includes(index) }"
                  @click="selectLetter(index, letter)"
                >
                  {{ letter }}
                </div>
              </div>
            </div>

            <div class="answer-area mb-4">
              <h6 class="fw-bold mb-2">Your Answer:</h6>
              <div class="answer-tiles d-flex justify-content-center gap-2">
                <div 
                  v-for="(letter, index) in userAnswer" 
                  :key="index"
                  class="answer-tile"
                  @click="removeLetter(index)"
                >
                  {{ letter || '_' }}
                </div>
              </div>
            </div>

            <div class="puzzle-actions text-center">
              <button 
                class="btn btn-outline-secondary me-2"
                @click="clearAnswer"
              >
                Clear
              </button>
              <button 
                class="btn btn-primary"
                @click="checkAnswer"
                :disabled="!isAnswerComplete"
              >
                Check Answer
              </button>
            </div>

            <div v-if="feedbackMessage && selectedPuzzle.type === 'word-scramble'" class="mt-3">
              <small :class="feedbackType === 'success' ? 'text-success' : 'text-danger'">{{ feedbackMessage }}</small>
            </div>
          </div>

          <!-- Math Puzzle -->
          <div v-else-if="selectedPuzzle.type === 'math'" class="puzzle-content">
            <div class="math-problem text-center mb-4">
              <h2 class="fw-bold">{{ selectedPuzzle.problem }}</h2>
            </div>
            
            <div class="answer-input text-center mb-4">
              <input 
                type="number" 
                class="form-control form-control-lg text-center"
                style="max-width: 200px; margin: 0 auto;"
                v-model="mathAnswer"
                placeholder="Your answer"
              >
            </div>

            <div class="puzzle-actions text-center">
              <button 
                class="btn btn-primary"
                @click="checkMathAnswer"
                :disabled="!mathAnswer"
              >
                Check Answer
              </button>
            </div>

            <div v-if="feedbackMessage && selectedPuzzle.type === 'math'" class="mt-3 text-center">
              <small :class="feedbackType === 'success' ? 'text-success' : 'text-danger'">{{ feedbackMessage }}</small>
            </div>
          </div>

          <!-- Logic Puzzle -->
          <div v-else-if="selectedPuzzle.type === 'logic'" class="puzzle-content">
            <div class="logic-question mb-4">
              <p class="fw-bold">{{ selectedPuzzle.question }}</p>
            </div>
            
            <div class="logic-options">
              <div 
                v-for="(option, index) in selectedPuzzle.options" 
                :key="index"
                class="option-card p-3 mb-2 cursor-pointer"
                :class="{ selected: logicAnswer === index }"
                @click="logicAnswer = index"
              >
                <div class="d-flex align-items-center">
                  <div class="option-letter me-3">{{ String.fromCharCode(65 + index) }}</div>
                  <span>{{ option }}</span>
                </div>
              </div>
            </div>

            <div class="puzzle-actions text-center mt-4">
              <button 
                class="btn btn-primary"
                @click="checkLogicAnswer"
                :disabled="logicAnswer === null"
              >
                Check Answer
              </button>
            </div>

            <div v-if="feedbackMessage && selectedPuzzle.type === 'logic'" class="mt-3">
              <small :class="feedbackType === 'success' ? 'text-success' : 'text-danger'">{{ feedbackMessage }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- Puzzle Stats -->
      <div class="col-lg-4">
        <div class="modern-card p-4">
          <h6 class="fw-bold mb-3">Puzzle Stats</h6>
          
          <div class="stat-item d-flex justify-content-between mb-3">
            <span>Puzzles Solved Today:</span>
            <span class="fw-bold">{{ todayStats.solved }}</span>
          </div>
          
          <div class="stat-item d-flex justify-content-between mb-3">
            <span>Total Time:</span>
            <span class="fw-bold">{{ formatTime(todayStats.totalTime) }}</span>
          </div>
          
          <div class="stat-item d-flex justify-content-between mb-3">
            <span>Accuracy:</span>
            <span class="fw-bold">{{ todayStats.accuracy }}%</span>
          </div>

          <hr>

          <h6 class="fw-bold mb-3">Achievements</h6>
          <div class="achievements">
            <div 
              v-for="achievement in achievements" 
              :key="achievement.id"
              class="achievement-item d-flex align-items-center mb-2"
            >
              <span class="achievement-icon me-2">{{ achievement.icon }}</span>
              <small>{{ achievement.name }}</small>
            </div>
          </div>

          <button class="btn btn-success w-100 mt-3" @click="completePuzzle">
            <i class="bi bi-check-lg me-2"></i>Complete Session
          </button>
        </div>
      </div>
    </div>

    <!-- Success Modal -->
    <div 
      class="modal fade" 
      :class="{ show: showSuccessModal, 'd-block': showSuccessModal }"
      v-if="showSuccessModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-body text-center p-4">
            <div class="success-icon mb-3">{{ resultIcon }}</div>
            <h5 class="fw-bold mb-2">{{ resultTitle }}</h5>
            <p class="text-muted mb-4">{{ successMessage }}</p>
            <button 
              class="btn btn-primary"
              @click="showSuccessModal = false"
            >
              {{ resultButtonLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showSuccessModal"></div>
  </div>
</template>

<script>
export default {
  name: 'PuzzleGame',
  data() {
    return {
      selectedPuzzle: null,
      elapsedTime: 0,
      timer: null,
      showSuccessModal: false,
      successMessage: '',
      resultTitle: 'Excellent Work!',
      resultIcon: '🎉',
      resultButtonLabel: 'Continue',
      feedbackMessage: '',
      feedbackType: 'neutral', // 'success' | 'error'
      
      // Word Scramble
      scrambledLetters: [],
      selectedLetters: [],
      userAnswer: [],
      
      // Math Puzzle
      mathAnswer: '',
      
      // Logic Puzzle
      logicAnswer: null,
      
      puzzles: [
        {
          id: 1,
          title: 'Word Scramble',
          description: 'Unscramble the letters to form a word',
          icon: '🔤',
          difficulty: 'Easy',
          type: 'word-scramble',
          word: 'FRIEND',
          scrambled: 'NDFERI',
          hint: 'Someone you care about'
        },
        {
          id: 2,
          title: 'Math Challenge',
          description: 'Solve the mathematical equation',
          icon: '🔢',
          difficulty: 'Medium',
          type: 'math',
          problem: '15 + 7 × 3 = ?',
          answer: 36
        },
        {
          id: 3,
          title: 'Logic Puzzle',
          description: 'Use logic to find the correct answer',
          icon: '🧠',
          difficulty: 'Hard',
          type: 'logic',
          question: 'If all roses are flowers, and some flowers are red, which statement must be true?',
          options: [
            'All roses are red',
            'Some roses might be red',
            'No roses are red',
            'All flowers are roses'
          ],
          correctAnswer: 1
        }
      ],
      
      todayStats: {
        solved: 2,
        totalTime: 450,
        accuracy: 85
      },
      
      achievements: [
        { id: 1, name: 'First Puzzle', icon: '🏆' },
        { id: 2, name: 'Speed Solver', icon: '⚡' },
        { id: 3, name: 'Logic Master', icon: '🧠' }
      ]
    }
  },
  computed: {
    isAnswerComplete() {
      if (this.selectedPuzzle?.type === 'word-scramble') {
        return this.userAnswer.length === this.selectedPuzzle.word.length && 
               this.userAnswer.every(letter => letter !== '')
      }
      return false
    }
  },
  mounted() {
    // Load puzzles from backend
    this.loadPuzzles()
  },
  beforeUnmount() {
    if (this.timer) {
      clearInterval(this.timer)
    }
  },
  methods: {
    async loadPuzzles() {
      const parseNumeric = (val) => {
        if (typeof val === 'number') return val
        if (typeof val === 'string' && val.trim() !== '' && !isNaN(Number(val))) return Number(val)
        return null
      }

      const mapToUi = (p, idx = 0) => ({
        id: p.id || idx,
        title: (p.type || '').toLowerCase() === 'math' ? 'Math Challenge' : (p.type || '').toLowerCase() === 'logic' ? 'Logic Puzzle' : 'Word Scramble',
        description: p.question || p.hint || 'Solve the puzzle',
        icon: p.type === 'math' ? '🔢' : p.type === 'logic' ? '🧠' : '🔤',
        difficulty: p.difficulty || 'Medium',
        type: (p.type || 'logic').toLowerCase(),
        // word-scramble
        word: (typeof p.answer === 'string' ? p.answer : (p.word || 'FRIEND')).toUpperCase(),
        scrambled: (typeof p.scrambled === 'string' ? p.scrambled : (p.hint || 'NDFERI')).toUpperCase(),
        hint: p.hint || '',
        // math
        problem: p.problem || p.question || '15 + 7 × 3 = ?',
        answer: (() => {
          const a = parseNumeric(p.answer)
          if (a !== null) return a
          const b = parseNumeric(p.correctAnswer)
          if (b !== null) return b
          return null
        })(),
        // logic
        question: p.question || '',
        options: Array.isArray(p.options) ? p.options : ['A', 'B', 'C', 'D'],
        correctAnswer: (() => {
          const ca = parseNumeric(p.correctAnswer)
          if (ca !== null) return ca
          const aa = parseNumeric(p.answer)
          if (aa !== null) return aa
          return 0
        })()
      })

      // Daily cache key
      const todayKey = `dailyPuzzles:${new Date().toISOString().slice(0, 10)}`
      try {
        const cached = localStorage.getItem(todayKey)
        if (cached) {
          const parsed = JSON.parse(cached)
          this.puzzles = parsed
          if (this.puzzles.length > 0) this.selectPuzzle(this.puzzles[0])
          return
        }
      } catch (e) { /* ignore cache parse errors */ }

      try {
        const { listPuzzles } = await import('../services/puzzleService.js')
        const data = await listPuzzles()
        const all = Array.isArray(data) ? data : []

        // Group by type and pick one random per type to get up to three (word-scramble, math, logic)
        const byType = all.reduce((acc, p) => {
          const t = (p.type || 'logic').toLowerCase()
          acc[t] = acc[t] || []
          acc[t].push(p)
          return acc
        }, {})

        const pickRandom = (arr) => arr[Math.floor(Math.random() * arr.length)]
        const selection = []
        if (byType['word-scramble']?.length) selection.push(pickRandom(byType['word-scramble']))
        if (byType['math']?.length) selection.push(pickRandom(byType['math']))
        if (byType['logic']?.length) selection.push(pickRandom(byType['logic']))

        // If fewer than 3 types available, fill with random unique puzzles
        const remaining = 3 - selection.length
        if (remaining > 0 && all.length > 0) {
          const usedIds = new Set(selection.map(p => p.id))
          const leftovers = all.filter(p => !usedIds.has(p.id))
          for (let i = 0; i < Math.min(remaining, leftovers.length); i++) {
            selection.push(pickRandom(leftovers))
          }
        }

        // Fallback to existing hardcoded list if backend returns nothing
        if (selection.length > 0) {
          this.puzzles = selection.map((p, idx) => mapToUi(p, idx))
        } else {
          // keep existing this.puzzles as-is
        }

        if (this.puzzles.length > 0) this.selectPuzzle(this.puzzles[0])
        try { localStorage.setItem(todayKey, JSON.stringify(this.puzzles)) } catch (e) { /* ignore cache errors */ }
      } catch (e) {
        console.error('Failed to load puzzles:', e)
        // Fallback to existing local puzzles if backend not available
        if (this.puzzles.length > 0) this.selectPuzzle(this.puzzles[0])
      }
    },
    selectPuzzle(puzzle) {
      this.selectedPuzzle = puzzle
      this.resetPuzzleState()
      this.startTimer()
      
      if (puzzle.type === 'word-scramble') {
        this.setupWordScramble()
      }
    },
    
    setupWordScramble() {
      this.scrambledLetters = this.selectedPuzzle.scrambled.split('')
      this.userAnswer = new Array(this.selectedPuzzle.word.length).fill('')
      this.selectedLetters = []
    },
    
    resetPuzzleState() {
      this.mathAnswer = ''
      this.logicAnswer = null
      this.elapsedTime = 0
    },
    
    startTimer() {
      if (this.timer) clearInterval(this.timer)
      this.timer = setInterval(() => {
        this.elapsedTime++
      }, 1000)
    },
    
    selectLetter(index, letter) {
      if (this.selectedLetters.includes(index)) return
      
      const emptyIndex = this.userAnswer.findIndex(l => l === '')
      if (emptyIndex !== -1) {
        this.userAnswer[emptyIndex] = letter
        this.selectedLetters.push(index)
      }
    },
    
    removeLetter(index) {
      if (this.userAnswer[index]) {
        const letterIndex = this.scrambledLetters.findIndex(
          (letter, i) => letter === this.userAnswer[index] && this.selectedLetters.includes(i)
        )
        this.selectedLetters = this.selectedLetters.filter(i => i !== letterIndex)
        this.userAnswer[index] = ''
      }
    },
    
    clearAnswer() {
      this.userAnswer = new Array(this.selectedPuzzle.word.length).fill('')
      this.selectedLetters = []
    },
    
    async checkAnswer() {
      const answer = this.userAnswer.join('')
      try {
        const { checkPuzzleAnswer } = await import('../services/puzzleService.js')
        const result = await checkPuzzleAnswer(this.selectedPuzzle.id, { answer })
        if (result.correct) {
          this.feedbackType = 'success'
          this.feedbackMessage = 'Correct! You unscrambled the word perfectly!'
          this.showResultModal(true, 'Great job!', '🎉')
          this.updateStats(true)
        } else {
          this.feedbackType = 'error'
          this.feedbackMessage = 'Not quite right. Try again!'
          this.showResultModal(false, 'Try Again', '🤔')
        }
      } catch (e) {
        this.feedbackType = 'error'
        this.feedbackMessage = 'Could not validate answer. Please try again.'
      }
    },
    
    async checkMathAnswer() {
      try {
        const { checkPuzzleAnswer } = await import('../services/puzzleService.js')
        const result = await checkPuzzleAnswer(this.selectedPuzzle.id, { answer: this.mathAnswer })
        if (result.correct) {
          this.feedbackType = 'success'
          this.feedbackMessage = 'Correct! Great math skills!'
          this.showResultModal(true, 'Correct Answer', '🎉')
          this.updateStats(true)
        } else {
          this.feedbackType = 'error'
          this.feedbackMessage = 'Not quite right. Try again!'
          this.showResultModal(false, 'Not Quite Right', '🤔')
        }
      } catch (e) {
        this.feedbackType = 'error'
        this.feedbackMessage = 'Could not validate answer. Please try again.'
      }
    },
    
    async checkLogicAnswer() {
      try {
        const { checkPuzzleAnswer } = await import('../services/puzzleService.js')
        const result = await checkPuzzleAnswer(this.selectedPuzzle.id, { choiceIndex: this.logicAnswer })
        if (result.correct) {
          this.feedbackType = 'success'
          this.feedbackMessage = 'Excellent logic! You got it right!'
          this.showResultModal(true, 'Well Done', '🎉')
          this.updateStats(true)
        } else {
          this.feedbackType = 'error'
          this.feedbackMessage = 'Not quite right. Think about it again!'
          this.showResultModal(false, 'Try Again', '🤔')
        }
      } catch (e) {
        this.feedbackType = 'error'
        this.feedbackMessage = 'Could not validate answer. Please try again.'
      }
    },
    
    showSuccess(message) {
      // kept for backwards-compatibility, call unified modal
      this.showResultModal(true, 'Excellent Work!', '🎉', message)
    },

    showResultModal(correct, title, icon, message) {
      this.resultTitle = title
      this.resultIcon = icon
      this.resultButtonLabel = correct ? 'Continue' : 'Try Again'
      this.successMessage = message || (correct ? 'Nice!' : 'Give it another shot!')
      this.showSuccessModal = true
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
    },
    
    updateStats(correct) {
      if (correct) {
        this.todayStats.solved++
        this.todayStats.totalTime += this.elapsedTime
      }
    },
    
    completePuzzle() {
      this.$router.push('/student-dashboard')
    },
    
    getDifficultyClass(difficulty) {
      const classes = {
        'Easy': 'bg-success',
        'Medium': 'bg-warning',
        'Hard': 'bg-danger'
      }
      return classes[difficulty] || 'bg-primary'
    },
    
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    }
  }
}
</script>

<style scoped>
.puzzle-card {
  border: 2px solid transparent;
  border-radius: 12px;
  transition: all 0.3s ease;
  background: var(--bg-primary);
}

.puzzle-card:hover {
  border-color: var(--role-primary);
  transform: translateY(-2px);
}

.puzzle-card.active {
  border-color: var(--role-primary);
  background: var(--role-primary);
  color: white;
}

.puzzle-icon {
  font-size: 2.5rem;
}

.letter-tile {
  width: 50px;
  height: 50px;
  border: 2px solid var(--border-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.2s ease;
  background: var(--bg-primary);
}

.letter-tile:hover {
  border-color: var(--role-primary);
  transform: scale(1.05);
}

.letter-tile.selected {
  background: var(--role-primary);
  color: white;
  border-color: var(--role-primary);
}

.answer-tile {
  width: 50px;
  height: 50px;
  border: 2px dashed var(--border-color);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.2rem;
  cursor: pointer;
  background: var(--bg-secondary);
}

.answer-tile:hover {
  background: var(--bg-primary);
}

.option-card {
  border: 2px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;
  background: var(--bg-primary);
}

.option-card:hover {
  border-color: var(--role-primary);
}

.option-card.selected {
  border-color: var(--role-primary);
  background: var(--role-primary);
  color: white;
}

.option-letter {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--role-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.success-icon {
  font-size: 3rem;
}

.achievement-icon {
  font-size: 1.2rem;
}

.cursor-pointer {
  cursor: pointer;
}

.modal.show {
  background: rgba(0, 0, 0, 0.5);
}

.modal-backdrop {
  background-color: rgba(0, 0, 0, 0.5);
}
</style> 